import React, { useEffect, useMemo, useRef } from "react";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Leaflet por defecto usa rutas a íconos PNG/svg que no se sirven con webpack.
// Apuntamos a CDN para evitar el marker roto.
const _icon = new L.Icon({
  iconUrl: "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

function Recenter({ lat, lng }) {
  const map = useMap();
  useEffect(() => {
    if (typeof lat === "number" && typeof lng === "number") {
      map.setView([lat, lng], map.getZoom() || 16, { animate: true });
    }
  }, [lat, lng, map]);
  return null;
}

/**
 * Mapa pequeño con marcador. Si `onChange` está presente, el marcador es draggable
 * y reporta nuevas coordenadas al soltar.
 *
 * Props:
 *   lat, lng (number)        — coordenadas iniciales (requerido para mostrar el mapa)
 *   onChange ({lat, lng})    — opcional; habilita arrastre del marcador
 *   height                   — alto en px (default 220)
 *   readOnly                 — fuerza marcador no draggable
 */
export default function MapPreview({ lat, lng, onChange, height = 220, readOnly = false }) {
  const markerRef = useRef(null);
  const draggable = !!onChange && !readOnly;

  const eventHandlers = useMemo(() => ({
    dragend() {
      const m = markerRef.current;
      if (m && onChange) {
        const p = m.getLatLng();
        onChange({ lat: p.lat, lng: p.lng });
      }
    },
  }), [onChange]);

  if (typeof lat !== "number" || typeof lng !== "number") return null;

  return (
    <div className="rounded-lg overflow-hidden border border-border" style={{ height }} data-testid="map-preview">
      <MapContainer center={[lat, lng]} zoom={16} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <Marker
          position={[lat, lng]}
          draggable={draggable}
          eventHandlers={eventHandlers}
          icon={_icon}
          ref={markerRef}
        />
        <Recenter lat={lat} lng={lng} />
      </MapContainer>
    </div>
  );
}

/** Genera una URL "abrir en Google Maps" óptima para web/mobile. */
export function googleMapsUrl(lat, lng) {
  if (typeof lat !== "number" || typeof lng !== "number") return null;
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${lat},${lng}`)}&travelmode=driving`;
}
